/* ============================================================
   TASK (2026-09-07): "the €3.99 Posting plan is charged immediately — no
   trial" must be reflected everywhere, and that must be driven by whether
   the plan ACTUALLY has a trial, never by scattering plan-key checks
   (`planKey === 'posting'`) across every screen that shows trial copy.

   Today the billing catalog (GET /api/billing/plans, see
   hooks/usePlanCatalog.js) does not expose a trial-days field per plan —
   trial length is a stripe-worker env var (STRIPE_TRIAL_DAYS, see
   lib/plans.js's TRIAL_DAYS comment), not part of the DB-driven display
   catalog. So the real signal, when a caller has one (a resolved plan
   object carrying `trialDays`/`trial_days`), wins; otherwise this falls
   back to the one plan_key known to never carry a trial today: 'posting'
   (migration 0114 — Scheduler tariff, PRICING-MODEL-V2.md §2; verified
   against workers/core/dathent-stripe-worker/src/stripe-universal-
   checkout.ts: createTrialCheckoutFromConfig — the only place trial_
   period_days is set — only runs when the caller passes trialSignup:true,
   and every 'posting' checkout call in this SPA omits it).

   Single helper — every surface that decides "show trial wording or not"
   for a plan should route through this, so a future plan gaining/losing a
   trial is a one-line change here instead of a per-screen audit.
   ============================================================ */

// The only plan_key with NO trial today, absent a real trial-days field
// from the catalog. Update this if another no-trial plan ships before the
// catalog exposes trial_days per row.
const PLAN_KEYS_WITHOUT_TRIAL = new Set(['posting']);

/**
 * Does this plan offer a free trial before the first charge?
 *
 * @param {string|{planKey?: string, trialDays?: number, trial_days?: number}} plan
 *   Either a bare backend planKey ('posting', 'growth', ...) or a resolved
 *   plan/display object that may carry a trial-days field.
 * @returns {boolean}
 */
export function planHasTrial(plan) {
  if (plan && typeof plan === 'object') {
    const trialDays = Number.isFinite(plan.trialDays)
      ? plan.trialDays
      : (Number.isFinite(plan.trial_days) ? plan.trial_days : undefined);
    if (trialDays !== undefined) return trialDays > 0;
    if (typeof plan.planKey === 'string') return !PLAN_KEYS_WITHOUT_TRIAL.has(plan.planKey);
    return true;
  }
  if (typeof plan === 'string') return !PLAN_KEYS_WITHOUT_TRIAL.has(plan);
  return true;
}

/**
 * P1 of GH-1133 (#1150): the extra startCheckout options a non-wall checkout
 * (the /app/plans page) must pass so a brand-new account gets the same trial
 * the card wall gives it (PlanSelectScreen:
 * startCheckout(plan, { trialSignup: !hasSubscriptionHistory, returnTo })).
 *
 * Returns { trialSignup: true, returnTo: '/app' } only for accessState 'new'
 * without subscription history, on a plan that has a trial. Returns null
 * for every other case, and the caller then makes exactly the no-trial call
 * it made before. returnTo '/app' lands the new trial where the wall's own
 * checkout returns (RequireActiveBilling picks up ?checkout=success there),
 * not on the pricing page.
 *
 * @param {string} planKey backend plan key
 * @param {object|null} entitlements GET /api/billing/entitlements (flat or { data })
 * @returns {{ trialSignup: true, returnTo: string } | null}
 */
export function newAccountTrialCheckoutOptions(planKey, entitlements) {
  const data = entitlements?.data || entitlements || null;
  if (!data || data.accessState !== 'new') return null;
  if (data.hasSubscriptionHistory === true) return null;
  if (!planHasTrial(planKey)) return null;
  return { trialSignup: true, returnTo: '/app' };
}

export default planHasTrial;
